/**
 * @architecture-header 1
 * @layer module
 * @domain authentication
 * @responsibility Present strict login and terms-gated Email or Google registration for both audiences.
 * @state-owner React Hook Form; host auth adapter owns credentials
 * @allowed-imports React Hook Form; Zod resolver; app session provider; module schema; shared i18n
 * @forbidden token storage; role selection; API entity cache
 * @public-api workspace shell gate
 * @side-effects host authentication calls
 * @tests frontend/packages/workspace-web/src/modules/auth/components/SignInScreen.test.tsx and frontend/packages/workspace-web/src/app/router/routes.test.tsx
 * @security-boundary unauthenticated visitor to Supabase Auth
 * @data-classification credentials in transit only
 * @trust-model successful authentication proves identity only
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useRef, useState } from 'react';
import { type UseFormReturn, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { WorkspaceAuthError, type WorkspaceAuthErrorKind } from '../../../app/config/environment';
import { useWorkspaceSession } from '../../../app/providers/SessionProvider';
import { VispoAnimatedMark, VispoBrandLogo } from '../../../shared/components/BrandMarks';
import { VispoInlineLoading } from '../../../shared/components/WorkspaceState';
import { useTranslator } from '../../../shared/model/use-workspace';
import {
  type EmailCodeValues,
  emailCodeSchema,
  type SignInValues,
  signInSchema,
} from '../schemas/sign-in';

function GoogleMark() {
  return (
    <svg aria-hidden="true" className="audience-login__google-mark" viewBox="0 0 24 24">
      <path
        fill="#4285F4"
        d="M21.6 12.23c0-.71-.06-1.4-.18-2.07H12v3.91h5.38a4.6 4.6 0 0 1-1.99 3.02v2.54h3.23c1.89-1.74 2.98-4.31 2.98-7.4Z"
      />
      <path
        fill="#34A853"
        d="M12 22c2.7 0 4.96-.9 6.62-2.37l-3.23-2.54c-.9.6-2.04.96-3.39.96-2.6 0-4.8-1.76-5.59-4.12H3.08v2.62A10 10 0 0 0 12 22Z"
      />
      <path
        fill="#FBBC05"
        d="M6.41 13.93A6.02 6.02 0 0 1 6.1 12c0-.67.12-1.32.31-1.93V7.45H3.08A10 10 0 0 0 2 12c0 1.61.39 3.14 1.08 4.55l3.33-2.62Z"
      />
      <path
        fill="#EA4335"
        d="M12 5.95c1.47 0 2.79.51 3.82 1.5l2.87-2.87A9.62 9.62 0 0 0 12 2a10 10 0 0 0-8.92 5.45l3.33 2.62C7.2 7.71 9.4 5.95 12 5.95Z"
      />
    </svg>
  );
}

function authErrorMessage(cause: unknown, fallback: string): string {
  if (cause instanceof WorkspaceAuthError) {
    return cause.kind === 'unknown-account'
      ? '找不到此 Vispo 帳號，請確認 Email 後改用已註冊的帳號。'
      : fallback;
  }
  return fallback;
}

function EmailCodeForm({
  email,
  error,
  form,
  errorState,
  onChangeEmail,
  onSubmit,
  resendBusy,
}: {
  email: string;
  error: string | null;
  errorState: WorkspaceAuthErrorKind | null;
  form: UseFormReturn<EmailCodeValues>;
  onChangeEmail(): void;
  onSubmit(values: EmailCodeValues): void | Promise<void>;
  resendBusy: boolean;
}) {
  return (
    <form className="audience-login__form" onSubmit={form.handleSubmit(onSubmit)}>
      <div className="audience-login__code-sent">
        <span>6 位數登入碼已寄到</span>
        <strong>{email}</strong>
        <button disabled={resendBusy} type="button" onClick={onChangeEmail}>
          更換 Email
        </button>
      </div>
      <label>
        <span>6 位數登入碼</span>
        <input
          autoComplete="one-time-code"
          inputMode="numeric"
          maxLength={6}
          pattern="[0-9]{6}"
          placeholder="000000"
          {...form.register('token')}
        />
        {form.formState.errors.token ? <em>{form.formState.errors.token.message}</em> : null}
      </label>
      {error ? (
        <p
          aria-live="polite"
          className="audience-login__error"
          data-auth-error-state={errorState ?? undefined}
          role="alert"
        >
          {error}
        </p>
      ) : null}
      <button
        className="audience-login__submit"
        disabled={form.formState.isSubmitting || resendBusy}
        type="submit"
      >
        {form.formState.isSubmitting ? <VispoInlineLoading label="驗證中…" /> : '驗證並登入'}
      </button>
    </form>
  );
}

function MobileAudienceLogin({
  audience,
  acceptedTerms,
  codeForm,
  error,
  errorState,
  form,
  googleBusy,
  onChangeEmail,
  onResend,
  onSubmitCode,
  onSubmitEmail,
  onToggleRegistration,
  onToggleTerms,
  onGoogle,
  pendingEmail,
  resendBusy,
  cooldownSeconds,
  registrationMode,
}: {
  audience: 'student' | 'teacher';
  acceptedTerms: boolean;
  codeForm: UseFormReturn<EmailCodeValues>;
  error: string | null;
  errorState: WorkspaceAuthErrorKind | null;
  form: UseFormReturn<SignInValues>;
  googleBusy: boolean;
  onChangeEmail(): void;
  onResend(): void | Promise<void>;
  onSubmitCode(values: EmailCodeValues): void | Promise<void>;
  onSubmitEmail(values: SignInValues): void | Promise<void>;
  onToggleRegistration(): void;
  onToggleTerms(accepted: boolean): void;
  onGoogle(): void | Promise<void>;
  pendingEmail: string | null;
  resendBusy: boolean;
  cooldownSeconds: number;
  registrationMode: boolean;
}) {
  const isTeacher = audience === 'teacher';
  return (
    <main
      className={`audience-login audience-login--${audience} audience-login--mobile`}
      data-composition="mobile"
    >
      <header className="audience-login__header">
        <a
          aria-label="回到 Vispo 首頁"
          className="audience-login__brand-link"
          href="https://vispo.app/"
        >
          <VispoBrandLogo className="audience-login__brand" size={34} variant="white" />
        </a>
        <Link
          className="audience-login__switch"
          to={isTeacher ? '/student/login' : '/teacher/login'}
        >
          {isTeacher ? '前往學生登入' : '前往老師登入'}
        </Link>
      </header>
      <section
        className="audience-login__mobile-card audience-login__card"
        aria-labelledby="audience-login-title"
      >
        <span className="audience-login__eyebrow">
          {isTeacher ? 'TEACHER SPACE' : 'STUDENT SPACE'}
        </span>
        <h1 id="audience-login-title">{isTeacher ? '老師帳號' : '學生帳號'}</h1>
        <p className="audience-login__mobile-welcome">
          {isTeacher ? '把每個學習瞬間，變成下一步。' : '很高興你加入我們。'}
        </p>
        {!isTeacher ? (
          <Link
            aria-label="已有機構帳號？"
            className="audience-login__organization"
            to="/student/org-login"
          >
            <span aria-hidden="true" className="audience-login__organization-icon">
              ⌁
            </span>
            <span>
              <strong>已有機構帳號？</strong>
              <small>使用機構提供的帳號與密碼登入</small>
            </span>
            <span aria-hidden="true">→</span>
          </Link>
        ) : (
          <div className="audience-login__teacher-note">
            <strong>你的教學工作區會在登入後準備完成</strong>
            <span>課程、活動與學生學習訊號都會在同一個地方。</span>
          </div>
        )}
        <div className="audience-login__auth">
          <h2>{registrationMode ? '建立 Vispo 帳號' : '使用 Vispo 帳號繼續'}</h2>
          <button
            className="audience-login__google"
            disabled={googleBusy || form.formState.isSubmitting || resendBusy}
            type="button"
            onClick={() => void onGoogle()}
          >
            {googleBusy ? (
              <VispoInlineLoading label="正在前往 Google…" />
            ) : (
              <>
                <GoogleMark />
                {registrationMode ? '使用 Google 建立帳號' : '使用 Google 登入'}
              </>
            )}
          </button>
          <div className="audience-login__divider">
            <span>或使用 Email</span>
          </div>
          {pendingEmail ? (
            <>
              <EmailCodeForm
                email={pendingEmail}
                error={error}
                errorState={errorState}
                form={codeForm}
                onChangeEmail={onChangeEmail}
                onSubmit={onSubmitCode}
                resendBusy={resendBusy}
              />
              {!registrationMode ? (
                <button
                  className="audience-login__resend bt"
                  disabled={resendBusy || cooldownSeconds > 0}
                  type="button"
                  onClick={() => void onResend()}
                >
                  {resendBusy ? (
                    <VispoInlineLoading label="正在重新寄送…" />
                  ) : cooldownSeconds > 0 ? (
                    `重新寄送登入碼（${cooldownSeconds} 秒後可用）`
                  ) : (
                    '重新寄送登入碼'
                  )}
                </button>
              ) : null}
            </>
          ) : (
            <form className="audience-login__form" onSubmit={form.handleSubmit(onSubmitEmail)}>
              <label>
                <span>Email</span>
                <input
                  autoComplete="email"
                  placeholder="name@example.com"
                  type="email"
                  {...form.register('email')}
                />
                {form.formState.errors.email ? (
                  <em>{form.formState.errors.email.message}</em>
                ) : null}
              </label>
              {error ? (
                <p
                  aria-live="polite"
                  className="audience-login__error"
                  data-auth-error-state={errorState ?? undefined}
                  role="alert"
                >
                  {error}
                </p>
              ) : null}
              <button
                className="audience-login__submit"
                disabled={form.formState.isSubmitting}
                type="submit"
              >
                {form.formState.isSubmitting ? (
                  <VispoInlineLoading label="正在寄送…" />
                ) : registrationMode ? (
                  '建立帳號並寄送登入碼'
                ) : (
                  '寄送登入碼'
                )}
              </button>
            </form>
          )}
          <button
            className="audience-login__switch-mode"
            disabled={resendBusy}
            type="button"
            onClick={onToggleRegistration}
          >
            {registrationMode ? '返回登入' : '建立新帳號'}
          </button>
          {registrationMode ? (
            <label className="audience-login__terms-check">
              <input
                checked={acceptedTerms}
                type="checkbox"
                onChange={(event) => onToggleTerms(event.target.checked)}
              />
              <span>
                我已閱讀並同意 <a href="https://vispo.app/terms">服務條款</a>與
                <a href="https://vispo.app/privacy">隱私權政策</a>（terms-v1 / privacy-v1）
              </span>
            </label>
          ) : null}
          <p className="audience-login__legal">
            繼續即表示你同意 Vispo 的 <a href="https://vispo.app/terms">服務條款</a>與
            <a href="https://vispo.app/privacy">隱私權政策</a>。
          </p>
        </div>
      </section>
    </main>
  );
}

export function SignInScreen({
  audience,
  returnTo,
}: {
  audience?: 'student' | 'teacher';
  returnTo?: string;
} = {}) {
  const { auth, refresh } = useWorkspaceSession();
  const t = useTranslator();
  const [error, setError] = useState<string | null>(null);
  const [errorState, setErrorState] = useState<WorkspaceAuthErrorKind | null>(null);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [resendBusy, setResendBusy] = useState(false);
  const resendBusyRef = useRef(false);
  const [cooldownUntil, setCooldownUntil] = useState<number | null>(null);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [registrationMode, setRegistrationMode] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const signInForm = useForm<SignInValues>({
    resolver: zodResolver(signInSchema),
    defaultValues: { email: '' },
  });
  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
  } = signInForm;
  const codeForm = useForm<EmailCodeValues>({
    resolver: zodResolver(emailCodeSchema),
    defaultValues: { token: '' },
  });

  useEffect(() => {
    if (cooldownUntil === null) {
      setCooldownSeconds(0);
      return;
    }
    const updateRemaining = () => {
      setCooldownSeconds(Math.max(0, Math.ceil((cooldownUntil - Date.now()) / 1000)));
    };
    updateRemaining();
    const timer = window.setInterval(updateRemaining, 1000);
    return () => window.clearInterval(timer);
  }, [cooldownUntil]);

  const submitEmail = async (values: SignInValues) => {
    setError(null);
    setErrorState(null);
    if (registrationMode && !acceptedTerms) {
      setError('請先勾選服務條款與隱私權政策，才能建立帳號');
      setErrorState('retryable');
      return;
    }
    try {
      if (registrationMode) {
        if (!auth.sendRegistrationCode) throw new WorkspaceAuthError('retryable');
        await auth.sendRegistrationCode(values);
      } else {
        await auth.sendEmailCode(values);
      }
      setPendingEmail(values.email);
      codeForm.reset({ token: '' });
      setCooldownUntil(registrationMode ? null : Date.now() + 60_000);
    } catch (cause) {
      const kind = cause instanceof WorkspaceAuthError ? cause.kind : 'retryable';
      setErrorState(kind);
      setError(authErrorMessage(cause, '無法寄送登入碼，請再試一次'));
    }
  };

  const resendCode = async () => {
    if (!pendingEmail || registrationMode || resendBusyRef.current || cooldownSeconds > 0) return;
    resendBusyRef.current = true;
    setError(null);
    setErrorState(null);
    setResendBusy(true);
    try {
      if (!auth.resendEmailCode) throw new WorkspaceAuthError('retryable');
      await auth.resendEmailCode({ email: pendingEmail });
      codeForm.reset({ token: '' });
      setCooldownUntil(Date.now() + 60_000);
    } catch (cause) {
      setErrorState('retryable');
      setError(authErrorMessage(cause, '無法寄送登入碼，請再試一次'));
    } finally {
      resendBusyRef.current = false;
      setResendBusy(false);
    }
  };

  const submitCode = async (values: EmailCodeValues) => {
    if (!pendingEmail || resendBusy) return;
    setError(null);
    setErrorState(null);
    try {
      if (registrationMode) {
        if (!auth.verifyRegistrationCode) throw new WorkspaceAuthError('retryable');
        await auth.verifyRegistrationCode({ email: pendingEmail, token: values.token });
      } else {
        await auth.verifyEmailCode({ email: pendingEmail, token: values.token });
      }
      if (audience) await auth.setPortalAudience?.(audience);
      await refresh();
    } catch (cause) {
      setErrorState('retryable');
      setError(authErrorMessage(cause, '登入碼不正確或已過期，請再試一次'));
    }
  };

  const signInWithGoogle = async () => {
    if (resendBusy) return;
    setError(null);
    setErrorState(null);
    if (registrationMode && !acceptedTerms) {
      setError('請先勾選服務條款與隱私權政策，才能建立帳號');
      setErrorState('retryable');
      return;
    }
    setGoogleBusy(true);
    try {
      const redirectPath = returnTo ?? (audience ? `/${audience}/login` : '/');
      if (audience) await auth.setPortalAudience?.(audience);
      if (registrationMode) {
        if (!auth.registerWithGoogle) throw new WorkspaceAuthError('retryable');
        await auth.registerWithGoogle({ redirectPath });
      } else {
        await auth.signInWithGoogle({ redirectPath });
      }
    } catch (cause) {
      setErrorState('retryable');
      setError(authErrorMessage(cause, 'Google 登入失敗'));
      setGoogleBusy(false);
    }
  };

  const isMobile =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(max-width: 820px)').matches;

  if (audience && isMobile) {
    return (
      <MobileAudienceLogin
        acceptedTerms={acceptedTerms}
        audience={audience}
        codeForm={codeForm}
        error={error}
        errorState={errorState}
        form={signInForm}
        googleBusy={googleBusy}
        onChangeEmail={() => {
          if (resendBusy) return;
          setError(null);
          setCooldownUntil(null);
          setPendingEmail(null);
        }}
        onGoogle={signInWithGoogle}
        onResend={resendCode}
        onSubmitCode={submitCode}
        onSubmitEmail={submitEmail}
        onToggleRegistration={() => {
          if (resendBusy) return;
          setRegistrationMode((current) => !current);
          setAcceptedTerms(false);
          setError(null);
          setErrorState(null);
          setCooldownUntil(null);
          setPendingEmail(null);
        }}
        onToggleTerms={setAcceptedTerms}
        pendingEmail={pendingEmail}
        resendBusy={resendBusy}
        cooldownSeconds={cooldownSeconds}
        registrationMode={registrationMode}
      />
    );
  }

  if (audience) {
    const isTeacher = audience === 'teacher';
    return (
      <main className={`audience-login audience-login--${audience}`}>
        <div aria-hidden="true" className="audience-login__orb audience-login__orb--one" />
        <div aria-hidden="true" className="audience-login__orb audience-login__orb--two" />
        <header className="audience-login__header">
          <a
            aria-label="回到 Vispo 首頁"
            className="audience-login__brand-link"
            href="https://vispo.app/"
          >
            <VispoBrandLogo className="audience-login__brand" size={34} variant="white" />
          </a>
          <Link
            className="audience-login__switch"
            to={isTeacher ? '/student/login' : '/teacher/login'}
          >
            {isTeacher ? '前往學生登入' : '前往老師登入'}
          </Link>
        </header>

        <section className="audience-login__card" aria-labelledby="audience-login-title">
          <div className="audience-login__story">
            <span className="audience-login__eyebrow">
              {isTeacher ? 'TEACHER SPACE' : 'STUDENT SPACE'}
            </span>
            <h1 id="audience-login-title">{isTeacher ? '老師帳號' : '學生帳號'}</h1>
            <p className="audience-login__welcome">
              <strong>
                {isTeacher ? (
                  <>
                    把每個學習瞬間，
                    <br />
                    變成下一步。
                  </>
                ) : (
                  '很高興你加入我們。'
                )}
              </strong>
              <span>
                {isTeacher
                  ? '登入後建立課程、掌握進度，陪學生走得更遠。'
                  : '登入後加入班級、完成活動，讓我們開始吧！'}
              </span>
            </p>
            {!isTeacher ? (
              <Link className="audience-login__organization" to="/student/org-login">
                <span aria-hidden="true" className="audience-login__organization-icon">
                  ⌁
                </span>
                <span>
                  <strong>已有機構帳號？</strong>
                  <small>使用機構提供的帳號與密碼登入</small>
                </span>
                <span aria-hidden="true">→</span>
              </Link>
            ) : (
              <div className="audience-login__teacher-note">
                <strong>你的教學工作區會在登入後準備完成</strong>
                <span>課程、活動與學生學習訊號都會在同一個地方。</span>
              </div>
            )}
          </div>

          <div className="audience-login__auth">
            <h2>{registrationMode ? '建立 Vispo 帳號' : '使用 Vispo 帳號繼續'}</h2>
            <button
              className="audience-login__google"
              disabled={googleBusy || isSubmitting || resendBusy}
              type="button"
              onClick={() => void signInWithGoogle()}
            >
              {googleBusy ? (
                <VispoInlineLoading label="正在前往 Google…" />
              ) : (
                <>
                  <GoogleMark />
                  {registrationMode ? '使用 Google 建立帳號' : '使用 Google 登入'}
                </>
              )}
            </button>
            <div className="audience-login__divider">
              <span>或使用 Email</span>
            </div>
            {pendingEmail ? (
              <>
                <EmailCodeForm
                  email={pendingEmail}
                  error={error}
                  errorState={errorState}
                  form={codeForm}
                  onChangeEmail={() => {
                    if (resendBusy) return;
                    setError(null);
                    setCooldownUntil(null);
                    setPendingEmail(null);
                  }}
                  onSubmit={submitCode}
                  resendBusy={resendBusy}
                />
                {!registrationMode ? (
                  <button
                    className="audience-login__resend bt"
                    disabled={resendBusy || cooldownSeconds > 0}
                    type="button"
                    onClick={() => void resendCode()}
                  >
                    {resendBusy ? (
                      <VispoInlineLoading label="正在重新寄送…" />
                    ) : cooldownSeconds > 0 ? (
                      `重新寄送登入碼（${cooldownSeconds} 秒後可用）`
                    ) : (
                      '重新寄送登入碼'
                    )}
                  </button>
                ) : null}
              </>
            ) : (
              <form className="audience-login__form" onSubmit={handleSubmit(submitEmail)}>
                <label>
                  <span>Email</span>
                  <input
                    autoComplete="email"
                    placeholder="name@example.com"
                    type="email"
                    {...register('email')}
                  />
                  {errors.email ? <em>{errors.email.message}</em> : null}
                </label>
                {error ? (
                  <p
                    aria-live="polite"
                    className="audience-login__error"
                    data-auth-error-state={errorState ?? undefined}
                    role="alert"
                  >
                    {error}
                  </p>
                ) : null}
                <button className="audience-login__submit" disabled={isSubmitting} type="submit">
                  {isSubmitting ? (
                    <VispoInlineLoading label="正在寄送…" />
                  ) : registrationMode ? (
                    '建立帳號並寄送登入碼'
                  ) : (
                    '寄送登入碼'
                  )}
                </button>
              </form>
            )}
            <button
              className="audience-login__switch-mode"
              disabled={resendBusy}
              type="button"
              onClick={() => {
                if (resendBusy) return;
                setRegistrationMode((current) => !current);
                setAcceptedTerms(false);
                setError(null);
                setErrorState(null);
                setPendingEmail(null);
              }}
            >
              {registrationMode ? '返回登入' : '建立新帳號'}
            </button>
            {registrationMode ? (
              <label className="audience-login__terms-check">
                <input
                  checked={acceptedTerms}
                  type="checkbox"
                  onChange={(event) => setAcceptedTerms(event.target.checked)}
                />
                <span>
                  我已閱讀並同意 <a href="https://vispo.app/terms">服務條款</a>與
                  <a href="https://vispo.app/privacy">隱私權政策</a>（terms-v1 / privacy-v1）
                </span>
              </label>
            ) : null}
            <p className="audience-login__legal">
              繼續即表示你同意 Vispo 的 <a href="https://vispo.app/terms">服務條款</a>與
              <a href="https://vispo.app/privacy">隱私權政策</a>。
            </p>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="entry-screen">
      <div className="joinbox" style={{ width: 'min(420px, 92vw)' }}>
        <VispoAnimatedMark className="jlogo" />
        <h3>登入 Vispo</h3>
        <span>登入後會先確認你的身分，再帶你進入對應的工作區。</span>
        <button
          className="bt"
          disabled={googleBusy || isSubmitting || resendBusy}
          style={{ marginTop: 16, width: '100%' }}
          type="button"
          onClick={() => void signInWithGoogle()}
        >
          {googleBusy ? <VispoInlineLoading label="正在前往 Google…" /> : '用 Google 繼續'}
        </button>
        {pendingEmail ? (
          <>
            <EmailCodeForm
              email={pendingEmail}
              error={error}
              errorState={errorState}
              form={codeForm}
              onChangeEmail={() => {
                if (resendBusy) return;
                setError(null);
                setCooldownUntil(null);
                setPendingEmail(null);
              }}
              onSubmit={submitCode}
              resendBusy={resendBusy}
            />
            <button
              className="bt"
              disabled={resendBusy || cooldownSeconds > 0}
              type="button"
              onClick={() => void resendCode()}
            >
              {resendBusy ? (
                <VispoInlineLoading label="正在重新寄送…" />
              ) : cooldownSeconds > 0 ? (
                `重新寄送登入碼（${cooldownSeconds} 秒後可用）`
              ) : (
                '重新寄送登入碼'
              )}
            </button>
          </>
        ) : (
          <form onSubmit={handleSubmit(submitEmail)} style={{ marginTop: 14, textAlign: 'left' }}>
            <div className="sec5" style={{ marginTop: 0 }}>
              Email
            </div>
            <input
              autoComplete="email"
              style={{ width: '100%', padding: '9px 12px' }}
              type="email"
              {...register('email')}
            />
            {errors.email ? <p className="jmsg">{errors.email.message}</p> : null}
            {error ? (
              <p
                aria-live="polite"
                className="jmsg"
                data-auth-error-state={errorState ?? undefined}
                role="alert"
              >
                {error}
              </p>
            ) : null}
            <button
              className="bt pri"
              disabled={isSubmitting}
              style={{ marginTop: 16, width: '100%' }}
              type="submit"
            >
              {isSubmitting ? <VispoInlineLoading label="正在寄送…" /> : '寄送登入碼'}
            </button>
          </form>
        )}
        <a
          className="bt"
          href="/student/org-login"
          style={{ display: 'block', marginTop: 12, textAlign: 'center', width: '100%' }}
        >
          {t('已有機構帳號？')}
        </a>
      </div>
    </main>
  );
}
